import React, { useEffect, useMemo, useRef } from "react";
import { motion } from "framer-motion";
import { applyFrameworkToTechnology } from "./api";
import { calculateSimilarity, calculateSubstitutability } from "./framework";
import SelectedTechModal from "./SelectedTechModal";
import Stamp from "./Stamp";
import { cn } from "./utils";

function AdjacentTechnology({
  comparisonTechName,
  tech,
  clr,
  onAnalysis,
  selectedTech,
  setSelectedTech,
  ...props
}) {
  const hasFetched = useRef(false);

  const fetchAnalysis = async () => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    try {
      const analysis = await applyFrameworkToTechnology(
        comparisonTechName,
        tech.name
      );

      onAnalysis(tech, analysis);
    } catch (error) {
      console.error("Error fetching analysis:", error);
    }
  };

  useEffect(() => {
    if (comparisonTechName && tech?.name) fetchAnalysis();
  }, []);

  const similarity = useMemo(() => calculateSimilarity(tech), [tech]);
  const transitionDifficulty = useMemo(
    () => tech?.transition_difficulty_factor_grade,
    [tech]
  );
  const substitutability = useMemo(
    () => calculateSubstitutability(tech, clr),
    [tech, clr]
  );

  if (!tech) return null;

  return (
    <>
      <div
        className="w-[240px] absolute cursor-pointer "
        style={{
          top: tech.position.top,
          left: tech.position.left,
          transform: "translate(-50%, -50%)",
        }}
        onClick={() => setSelectedTech(tech)}
      >
        <motion.div
          className="bg-white shadow-sm rounded-md flex flex-col items-center relative transform transition-transform duration-300 hover:scale-105"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{
            duration: 0.3,
            delay: props.animationDelay,
          }}
        >
          <div className="absolute -top-4 -right-4">
            <Stamp
              grade={substitutability?.grade}
              className={cn(
                "absolute",
                substitutability?.grade ? "-top-4 -right-4" : "-top-2 -right-2"
              )}
            />
          </div>
          <div className="flex flex-col items-center overflow-y-auto px-6 py-4">
            <h3 className="text-sm font-semibold mb-2">{tech.name}</h3>
            <p className="text-xs text-gray-600 mb-2">
              {tech.short_eli5_description}
            </p>
          </div>
        </motion.div>
      </div>
      <SelectedTechModal
        show={selectedTech === tech}
        tech={tech}
        similarity={similarity}
        transitionDifficulty={transitionDifficulty}
        substitutability={substitutability}
        comparisonTechName={comparisonTechName}
        onClose={() => setSelectedTech(null)}
      />
    </>
  );
}

export default AdjacentTechnology;
